/*
  NUDIENT: Landpanelen, CountryDrawer, paket C2, M-079. Plan:
  nudient/PLAN-C2-landvaljare.md punkt 7 till 10 och 14.

  Horizons theme-drawer, assets/theme-drawer.js, öppnar och stänger panelen,
  flyttar fokus till X:et, håller fokus i panelen, stänger vid Esc och lämnar
  fokus tillbaka. Det här skriptet gör bara det Horizon saknar:

  - Öppnar panelen från EN / SEK i sidhuvudet och från "Sweden · SEK" i
    mobilpanelen, i stället för Horizons egen landväljare, M-079 punkt 3 och
    fråga 11. Klicket fångas innan Horizon får det, så att ingen av Horizons
    filer ändras. Enter och mellanslag ger ett klick på knappen och fångas
    likadant.
    - EN / SEK får aria-controls och aria-expanded mot panelen.
    - Marknadsväljaren i sidfoten, B3, har data-nudient-country-open och
      öppnar panelen på samma sätt, M-079 fråga 9 och M-081. Den har
      aria-controls i sin markup och får aria-expanded här.
    - Från mobilpanelen stängs mobilpanelen först, på samma sätt som
      assets/nudient-header.js gör när sök, konto eller varukorg trycks,
      tillägget till M-070 2 oktober 2026. Fokus läggs på menyknappen innan
      panelen öppnas, så att Horizon lämnar tillbaka fokus dit när panelen
      stängs. Raden som öppnade panelen ligger i den stängda mobilpanelen.
  - Stänger panelen vid tryck utanför från 990, där Horizons låda inte är
    modal, som assets/nudient-cart.js i C1.
  - Skriver om värdet som syns i rutan när valet ändras, och skickar
    Shopifys formulär, M-079 fråga 8. Med musen och på telefonen skickas
    formuläret när valet görs. Med tangentbordet byter pilarna i vissa
    webbläsare valet utan att listan öppnas, och då skickas formuläret först
    vid Enter eller när rutan lämnas, så att varje pil inte laddar om sidan.
    Esc ångrar ett val som inte har skickats.

  Värden som inte är tokens:
  - country-drawer: panelens id, snippets/nudient-country-drawer.liquid.
  - Enter: tangenten som skickar formuläret efter ett val med tangentbordet.
  - Escape: tangenten som ångrar ett val som inte har skickats.
  - Tab: tangenten som lämnar rutan utan att räknas som ett val med
    tangentbordet.
*/

import { removeTrapFocus } from '@theme/focus';

const ID = 'country-drawer';
const TRIGGERS = '.dropdown-localization__button, #HeaderDrawer-localization, [data-nudient-country-open]';

const drawer = () => document.getElementById(ID);
const dialog = () => drawer()?.querySelector('dialog');
const headerButton = () => document.querySelector('.dropdown-localization__button');

/* Stänger mobilpanelen utan att vänta på dess övergång, som reset i
   assets/nudient-header.js, och ger menyknappen fokus. */
const closeMobileDrawer = () => {
  const details = document.querySelector('#Details-menu-drawer-container');
  if (!details?.open) return;
  removeTrapFocus();
  details.querySelector('.menu-drawer')?.classList.remove('menu-drawer--has-submenu-opened');
  const reset = (element) => {
    element.classList.remove('menu-open');
    element.removeAttribute('open');
    element.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
  };
  details.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
  reset(details);
  details.querySelector(':scope > summary')?.focus({ preventScroll: true });
};

const open = (trigger) => {
  const element = drawer();
  if (!element || typeof element.open !== 'function') return false;
  if (trigger.id === 'HeaderDrawer-localization') closeMobileDrawer();
  element.open();
  return true;
};

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest(TRIGGERS);
    if (!(trigger instanceof HTMLElement) || !drawer()) return;
    event.preventDefault();
    event.stopPropagation();
    open(trigger);
  },
  true
);

/*
  Tryck utanför panelen från 990. Horizon stänger bara vid tryck på bakgrunden
  i modalt läge, assets/theme-drawer.js #onBackdropClick. Från 990 ligger den
  mörka ytan över sidan, och ett tryck där stänger panelen med Horizons
  close(), utan att trycket når länken under.
*/
document.addEventListener(
  'click',
  (event) => {
    const element = drawer();
    const panel = dialog();
    if (!element || !panel?.open || panel.matches(':modal')) return;
    if (!(event.target instanceof Node) || panel.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest(TRIGGERS)) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof element.close === 'function') element.close();
  },
  true
);

/* EN / SEK säger att den styr panelen, och om panelen är öppen. */
const syncButton = () => {
  if (!drawer()) return;
  const expanded = drawer().hasAttribute('open') ? 'true' : 'false';
  const button = headerButton();
  if (button) {
    button.setAttribute('aria-controls', ID);
    button.setAttribute('aria-expanded', expanded);
  }
  document.querySelectorAll('[data-nudient-country-open]').forEach((element) => {
    element.setAttribute('aria-expanded', expanded);
  });
};

document.addEventListener('theme-drawer:open', syncButton);
document.addEventListener('theme-drawer:close', syncButton);

/* Rutorna: värdet som syns, och när formuläret skickas */
const submit = (select) => {
  const form = select.form;
  if (!form || select.dataset.nudientSubmitted === 'true') return;
  select.dataset.nudientSubmitted = 'true';
  form.submit();
};

const showValue = (select) => {
  const value = select.closest('.nudient-select-stacked')?.querySelector('[data-nudient-select-value]');
  const option = select.selectedOptions[0];
  if (value && option) value.textContent = option.textContent.trim();
};

const start = () => {
  syncButton();
  document.querySelectorAll(`#${ID} [data-nudient-country-select]`).forEach((select) => {
    if (!(select instanceof HTMLSelectElement)) return;
    const initial = select.value;
    let keyboard = false;

    select.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        if (select.value !== initial) {
          event.preventDefault();
          submit(select);
        }
        return;
      }
      if (event.key === 'Escape') {
        // Esc ångrar ett val som inte har skickats, innan Horizon stänger panelen.
        if (select.value !== initial) {
          select.value = initial;
          showValue(select);
        }
        return;
      }
      if (event.key !== 'Tab') keyboard = true;
    });
    select.addEventListener('pointerdown', () => {
      keyboard = false;
    });
    select.addEventListener('change', () => {
      showValue(select);
      if (!keyboard) submit(select);
    });
    select.addEventListener('blur', () => {
      if (select.value !== initial) submit(select);
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
